import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, ShieldX, Loader2 } from "lucide-react";

export default function MfaSettings() {
  const { user, refresh } = useAuth();
  const [setup, setSetup] = useState(null); // { secret, otpauth_uri, qrSvg }
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const begin = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/setup");
      // Generate QR via Google charts API (no extra dep)
      const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(data.otpauth_uri)}&size=220x220&margin=1`;
      setSetup({ ...data, qrUrl });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to start MFA setup"); }
    finally { setBusy(false); }
  };

  const enable = async () => {
    setBusy(true);
    try {
      await api.post("/auth/mfa/enable", { code });
      toast.success("MFA enabled — your account is more secure");
      setSetup(null); setCode("");
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Verification failed"); }
    finally { setBusy(false); }
  };

  const disable = async () => {
    if (!code) { toast.error("Enter your current 6-digit code"); return; }
    setBusy(true);
    try {
      await api.post("/auth/mfa/disable", { code });
      toast.success("MFA disabled");
      setCode("");
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Verification failed"); }
    finally { setBusy(false); }
  };

  const enabled = !!user?.mfa_enabled;

  return (
    <div className="p-8 max-w-2xl space-y-6" data-testid="mfa-settings">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-black">Two-factor authentication</h1>
        <p className="text-muted-foreground">Protect your TileERP account with a TOTP authenticator (Google Authenticator, 1Password, Authy).</p>
      </div>

      <div className="surface-card p-6">
        <div className="flex items-center gap-3">
          {enabled
            ? <ShieldCheck className="w-6 h-6 text-emerald-600" />
            : <ShieldX className="w-6 h-6 text-muted-foreground" />}
          <div>
            <div className="font-display font-bold">{enabled ? "MFA is ON" : "MFA is OFF"}</div>
            <div className="text-sm text-muted-foreground">{enabled ? "You'll be asked for a code on every sign-in." : "Anyone with your password can sign in."}</div>
          </div>
        </div>
      </div>

      {!enabled && !setup && (
        <Button onClick={begin} disabled={busy} data-testid="mfa-begin-btn">
          {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Preparing</> : "Enable MFA"}
        </Button>
      )}

      {!enabled && setup && (
        <div className="surface-card p-6 space-y-4" data-testid="mfa-setup-card">
          <div className="label-overline">Step 1 · Scan the QR with your authenticator</div>
          <img src={setup.qrUrl} alt="MFA QR" className="w-56 h-56 border border-border rounded-md" data-testid="mfa-qr-image" />
          <div className="text-xs text-muted-foreground">
            Or paste this secret manually: <code className="font-mono text-foreground select-all" data-testid="mfa-secret">{setup.secret}</code>
          </div>
          <div className="space-y-2">
            <Label>Step 2 · Enter the 6-digit code shown by your app</Label>
            <Input value={code} onChange={(e) => setCode(e.target.value)} maxLength={6} placeholder="123 456" data-testid="mfa-code-input" />
          </div>
          <div className="flex gap-2">
            <Button onClick={enable} disabled={busy || code.length < 6} data-testid="mfa-enable-btn">{busy ? "Verifying…" : "Verify & turn on MFA"}</Button>
            <Button variant="outline" onClick={() => { setSetup(null); setCode(""); }}>Cancel</Button>
          </div>
        </div>
      )}

      {enabled && (
        <div className="surface-card p-6 space-y-3" data-testid="mfa-disable-card">
          <div className="label-overline">Disable MFA</div>
          <p className="text-sm text-muted-foreground">To turn off MFA, confirm with your current authenticator code.</p>
          <div className="space-y-2">
            <Label>Current 6-digit code</Label>
            <Input value={code} onChange={(e) => setCode(e.target.value)} maxLength={6} placeholder="123 456" data-testid="mfa-disable-input" />
          </div>
          <Button variant="destructive" onClick={disable} disabled={busy} data-testid="mfa-disable-btn">{busy ? "Disabling…" : "Disable MFA"}</Button>
        </div>
      )}
    </div>
  );
}
